import { graphql, useStaticQuery } from "gatsby";
import React, { useContext } from "react";
import DynamicComponent from "../DynamicComponent/DynamicComponent.component";
import BreakpointChecker from "../BreakpointChecker/BreakpointChecker.util";
import renderBloks from "../../../utils/renderBloks.util";
import Blok from "../../../interfaces/Storyblok/Blok.interface";
import { layoutClassNames } from "../../../styles/ClassNames/global.classes";
import LayoutContext from "../../../contexts/LayoutContext";
import BreadcrumbBar from "../../bloks/BreadcrumbBar/BreadcrumbBar";
import GlobalTypeformPopup from "../GlobalTypeformPopup/GlobalTypeformPopup.component";

interface FormPopupBlok extends Blok {
  enable_form?: boolean;
  typeform_id?: string;
  delay?: number;
  hide_on_specific_pages?: string[];
}

interface LayoutProps {
  children: Blok[][] | undefined;
  countdown: Blok | undefined;
  bloks: {
    page_hero: Blok[];
    page_body: Blok[];
    page_footer: Blok[];
  };
  breadcrumb_override?: string;
  hasBackground?: boolean;
  contentType: string;
}

const getBlok = (content: any): Blok | undefined => {
  if (content && content?.edges && content?.edges.length > 0) {
    const blok = content?.edges?.filter((edge: any) =>
      edge.node.full_slug.includes(process.env.GATSBY_DOMAIN_FOLDER),
    )[0]?.node?.content;
    if (blok) {
      return JSON.parse(blok);
    }
    return undefined;
  }
  return undefined;
};

const Layout: React.FC<LayoutProps> = ({
  children,
  countdown,
  bloks,
  breadcrumb_override,
  hasBackground,
  contentType,
}) => {
  const { header, footer, newsletter, formPopup } = useStaticQuery(graphql`
    {
      header: allStoryblokEntry(filter: { field_component: { eq: "Header" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      footer: allStoryblokEntry(filter: { field_component: { eq: "Footer" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      newsletter: allStoryblokEntry(filter: { field_component: { eq: "Newsletter" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      formPopup: allStoryblokEntry(filter: { field_component: { eq: "form_popup" } }) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
    }
  `);

  const headerBlok = getBlok(header);
  const footerBlok = getBlok(footer);
  const newsletterBlok = getBlok(newsletter);
  const { page_hero, page_body, page_footer } = bloks;
  const formPopupBlok = getBlok(formPopup) as FormPopupBlok | undefined;
  const breadcrumbs = useContext(LayoutContext);

  return (
    <div className={layoutClassNames}>
      <BreakpointChecker />
      <header>{headerBlok && <DynamicComponent blok={headerBlok} />}</header>
      <main>
        {/* Fallback h1 heading for pages that don't have one */}
        <h1 className="sr-only">Sherpa Kids</h1>
        {renderBloks(page_hero, countdown)}
        {breadcrumbs && (
          <BreadcrumbBar
            {...breadcrumbs}
            breadcrumb_override={breadcrumb_override}
            hasBackground={Boolean(hasBackground)}
          />
        )}
        {renderBloks(page_body)}
        {renderBloks(page_footer)}
        {formPopupBlok && formPopupBlok.enable_form && formPopupBlok.typeform_id && (
          <GlobalTypeformPopup
            typeformId={formPopupBlok.typeform_id}
            delay={formPopupBlok.delay || 5}
            respectCookieConsent={false}
            hide_on_specific_pages={formPopupBlok.hide_on_specific_pages || []}
            preventReopenDays={0}
          />
        )}
      </main>
      <footer>
        {newsletterBlok && <DynamicComponent blok={newsletterBlok} customProps={{ contentType: contentType }} />}
        {footerBlok && <DynamicComponent blok={footerBlok} />}
      </footer>
    </div>
  );
};

export default Layout;
